import React, { useContext, useState } from 'react';
import noop from 'lodash/noop';
import pullAt from 'lodash/pullAt';
import { useGlobalUser } from '@sparknz/journeys-common';
import { trackCustomLink } from '@sparknz/tracking';

import useGuestLogin from 'shared/hooks/useGuestLogin';
import { SelectedPlan } from '../../typings/plans';
import { replaceAt } from '../../utils/replaceAt';

export interface SelectedPlansContextValue {
  getSelectedPlanQuantity: (offerId: string) => number;
  selectedPlans: SelectedPlan[];
  increaseSelectedPlan: (
    offerId: string,
    planName: string,
    isTeamUpEligible: boolean,
  ) => void;
  decreaseSelectedPlan: (offerId: string, planName: string) => void;
  setSelectedPlans: (selectedPlans: SelectedPlan[]) => void;
  setSelectedPlanByQueryParameters: (
    offerId: string,
    planName: string,
    quantity: number,
  ) => void;
  selectIndividualPlan: (
    offerId: string,
    planName: string,
    details: SelectedPlanConfig,
  ) => Promise<void>;
  isSelectedPlansLayerOpen: boolean;
  openSelectedPlansLayer(): void;
  closeSelectedPlansLayer(): void;
  totalSelectedPlansQuantity: number;
  totalSelectedTeamUpEligiblePlansQuantity: number;
}

interface SelectedPlanConfig
  extends Partial<
    Pick<
      SelectedPlan,
      | 'quantity'
      | 'categoryIdentifiers'
      | 'offerEligibilityLevel'
      | 'isTeamUpEligible'
      | 'isPrimary'
      | 'relatedOfferId'
      | 'relatedOfferIds'
    >
  > {
  skipGuestLogin?: boolean;
}

const SelectedPlansContext = React.createContext<SelectedPlansContextValue>({
  selectedPlans: [],
  setSelectedPlans: noop,
  setSelectedPlanByQueryParameters: noop,
  getSelectedPlanQuantity: () => 0,
  increaseSelectedPlan: noop,
  decreaseSelectedPlan: noop,
  selectIndividualPlan: async () => {},
  isSelectedPlansLayerOpen: false,
  openSelectedPlansLayer: noop,
  closeSelectedPlansLayer: noop,
  totalSelectedPlansQuantity: 0,
  totalSelectedTeamUpEligiblePlansQuantity: 0,
});

const SELECT_PLAN = 'Select plan';

export default function SelectedPlansProvider({
  children,
}: React.PropsWithChildren<{}>) {
  const [selectedPlans, setSelectedPlans] = useState<SelectedPlan[]>([]);
  const [isSelectedPlansLayerOpen, setIsSelectedPlansLayerOpen] =
    useState(false);

  const {
    authenticatedInfo: { isLoggedIn },
    refetchUser,
  } = useGlobalUser();
  const { tryGuestLogin } = useGuestLogin();

  function getSelectedPlanQuantity(offerId: string) {
    const foundSelectedPlan = selectedPlans.find(
      (selectedPlan) => selectedPlan.offerId === offerId,
    );
    if (!foundSelectedPlan) {
      return 0;
    }

    return foundSelectedPlan.quantity;
  }

  function setSelectedPlanQuantity(
    offerId: string,
    quantity: number,
    planName: string,
    isTeamUpEligible?: boolean,
  ) {
    const selectedPlanIndex = selectedPlans.findIndex(
      (selectedPlan) => selectedPlan.offerId === offerId,
    );

    const newSelectedPlan = {
      ...selectedPlans[selectedPlanIndex],
      offerId,
      quantity,
      planName,
      isTeamUpEligible:
        isTeamUpEligible ?? selectedPlans[selectedPlanIndex].isTeamUpEligible,
    };

    const newSelectedPlans = replaceAt(
      selectedPlans,
      selectedPlanIndex,
      newSelectedPlan,
    );
    setSelectedPlans(newSelectedPlans);
  }

  async function setSelectedPlanByQueryParameters(
    offerId: string,
    planName: string,
    quantity: number,
  ) {
    trackCustomLink(SELECT_PLAN);
    if (!isLoggedIn) {
      await tryGuestLogin();
      await refetchUser();
    }

    // Assume query params are only coming from the team up page
    const isTeamUpEligible = true;

    setSelectedPlanQuantity(offerId, quantity, planName, isTeamUpEligible);
  }

  async function increaseSelectedPlan(
    offerId: string,
    planName: string,
    isTeamUpEligible: boolean,
  ) {
    trackCustomLink(SELECT_PLAN);
    if (!isLoggedIn) {
      await tryGuestLogin();
      await refetchUser();
    }
    const currentPlanQuantity = getSelectedPlanQuantity(offerId);
    if (currentPlanQuantity === 0) {
      setSelectedPlans([
        ...selectedPlans,
        { offerId, quantity: 1, planName, isTeamUpEligible },
      ]);
      return;
    }

    setSelectedPlanQuantity(offerId, currentPlanQuantity + 1, planName);
  }

  async function decreaseSelectedPlan(offerId: string, planName: string) {
    trackCustomLink(SELECT_PLAN);
    if (!isLoggedIn) {
      await tryGuestLogin();
      await refetchUser();
    }
    const currentPlanQuantity = getSelectedPlanQuantity(offerId);

    if (currentPlanQuantity <= 0) {
      return;
    }
    if (currentPlanQuantity === 1) {
      const selectedPlanIndex = selectedPlans.findIndex(
        (selectedPlan) => selectedPlan.offerId === offerId,
      );

      const newSelectedPlans = [...selectedPlans];
      pullAt(newSelectedPlans, selectedPlanIndex);

      setSelectedPlans(newSelectedPlans);
      return;
    }

    setSelectedPlanQuantity(offerId, currentPlanQuantity - 1, planName);
  }

  const selectIndividualPlan = async (
    offerId: string,
    planName: string,
    {
      quantity,
      categoryIdentifiers,
      offerEligibilityLevel,
      isTeamUpEligible,
      isPrimary,
      relatedOfferId,
      relatedOfferIds,
      skipGuestLogin,
    }: SelectedPlanConfig = {},
  ) => {
    trackCustomLink(SELECT_PLAN);
    if (!(isLoggedIn || skipGuestLogin)) {
      await tryGuestLogin({ shouldThrowError: true });
      await refetchUser();
    }

    const plan = {
      offerId,
      quantity: quantity ?? 1,
      planName,
      categoryIdentifiers,
      offerEligibilityLevel,
      isTeamUpEligible,
      isPrimary,
      relatedOfferId,
      relatedOfferIds,
    };

    if (isPrimary) {
      setSelectedPlans([plan]);
      return;
    }

    if (getSelectedPlanQuantity(offerId) === 0) {
      setSelectedPlans([...selectedPlans, plan]);
      return;
    }

    const updatedPlans = selectedPlans.map((selectedPlan) =>
      selectedPlan.offerId === offerId ? plan : selectedPlan,
    );

    setSelectedPlans(updatedPlans);
  };

  const openSelectedPlansLayer = () => setIsSelectedPlansLayerOpen(true);

  const closeSelectedPlansLayer = () => setIsSelectedPlansLayerOpen(false);

  const totalSelectedPlansQuantity = selectedPlans.reduce(
    (total, selectedPlan) => total + selectedPlan.quantity,
    0,
  );

  const totalSelectedTeamUpEligiblePlansQuantity = selectedPlans.reduce(
    (total, selectedPlan) =>
      total + (selectedPlan.isTeamUpEligible ? selectedPlan.quantity : 0),
    0,
  );

  const value = {
    selectedPlans,
    setSelectedPlans,
    setSelectedPlanByQueryParameters,
    getSelectedPlanQuantity,
    increaseSelectedPlan,
    decreaseSelectedPlan,
    selectIndividualPlan,
    isSelectedPlansLayerOpen,
    openSelectedPlansLayer,
    closeSelectedPlansLayer,
    totalSelectedPlansQuantity,
    totalSelectedTeamUpEligiblePlansQuantity,
  };

  return (
    <SelectedPlansContext.Provider value={value}>
      {children}
    </SelectedPlansContext.Provider>
  );
}

export function useSelectedPlans() {
  const context = useContext(SelectedPlansContext);
  if (!context) {
    throw new Error(
      'useSelectedPlans should be used inside SelectedPlansProvider',
    );
  }
  return context;
}
